/**
 * Web entrypoint (008 US2). Constructs the WEB PlatformIO (IndexedDB-backed)
 * and the WASM EngineBinding driving the render Web Worker, then mounts the
 * shared App. Rendering happens in-browser, off the main thread.
 */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createWebPlatform, createIdbLocalStore } from "@genz/platform";
import { createWasmEngine, type WorkerLike } from "@genz/engine-bind";
import { App, createAppServices, ClientLogger, createLateBoundTelemetryClient, renderBootFailure, useSessionStore } from "@genz/shared-ui";
import "./styles.css";

// `||`, NOT `??`: an UNSET repo variable in CI expands to the EMPTY STRING, which
// `??` happily accepts — every API call then resolves against the app origin and a
// signed, published release silently reaches no cloud at all (audit S45).
const BASE_URL = import.meta.env.VITE_CLOUD_URL || "http://localhost:8000";

// Late-bound logger handle for the worker fault handlers installed before construction.
/** Shipped build number. Module scope so the boot-failure card below — which runs when
 *  `main()` never reached its own body — can still report it. */
const APP_VERSION = "0.2.146";

let loggerRef: ClientLogger | undefined;

async function main() {
  const io = createWebPlatform(createIdbLocalStore());

  // The render worker hosts the REAL genz-engine WASM (built by scripts/build-wasm.sh).
  // There is NO mock fallback: if wasm init fails the worker rejects every request with
  // "render engine unavailable: …" so the failure is visible instead of silently faked.
  // The main thread talks to it through the WasmEngineBinding.
  const worker = new Worker(new URL("./worker/render.worker.ts", import.meta.url), { type: "module" });
  const engine = createWasmEngine({ worker: worker as unknown as WorkerLike });
  // A hard worker crash (wasm panic, OOM, script load failure) produced NO log anywhere —
  // renders just started rejecting. Capture both worker fault channels; the logger is
  // constructed a few lines below, so route through a late-bound reference.
  worker.onerror = (e) => loggerRef?.log("error", `Render worker crashed: ${e.message ?? "unknown"} (${e.filename ?? "?"}:${e.lineno ?? "?"})`);
  worker.onmessageerror = () => loggerRef?.log("error", "Render worker message deserialization failed (structured-clone error)");

  const deviceId = await io.deviceFingerprint();

  // Construct the client logger BEFORE createAppServices so we can wire
  // onRequestObserved into the JsonClient at construction time.
  // The logger reaches the cloud client through a late-bound handle: `services` does not
  // exist yet, and a flush during the await below must not read it in its dead zone.
  const lateClient = createLateBoundTelemetryClient();

  const logger = new ClientLogger({
    store: io.localStore,
    sendEvents: (events) => lateClient.sendEvents(events),
    platform: "web",
    appVersion: APP_VERSION,
    deviceId,
    sessionId: crypto.randomUUID(),
    isOnline: () => io.isOnline(),
    isAuthenticated: () => lateClient.isAuthenticated(),
    onConnectivityChange: (cb) => io.onConnectivityChange(cb),
  });
  loggerRef = logger;

  const services = await createAppServices({
    io,
    engine,
    baseUrl: BASE_URL,
    appVersion: APP_VERSION,
    onRequestObserved: (r) => logger.observeRequest(r),
    logger,
  });

  // Bind the real client now that it exists; from here the logger can authenticate and send.
  lateClient.bind(services.client);

  // Seed logger with any already-restored session (covers events captured before
  // the first subscription fires — e.g. during boot with a persisted session).
  const _initialSession = useSessionStore.getState().session;
  if (_initialSession?.customerId) logger.setCustomer(_initialSession.customerId);

  // Update logger.setCustomer whenever the session changes (after login).
  useSessionStore.subscribe((state) => {
    if (state.session?.customerId) logger.setCustomer(state.session.customerId);
  });

  const root = document.getElementById("root")!;
  createRoot(root).render(
    <StrictMode>
      <App services={{ ...services, logger }} />
    </StrictMode>,
  );
}

main().catch((e) => {
  // Boot failures used to be a white screen with ZERO logs (no handlers installed yet) —
  // and, just as importantly, nothing ON SCREEN. The static card is plain DOM: React may
  // be exactly what failed here.
  console.error("[genz] boot failed:", e);
  loggerRef?.log("error", `Boot failed: ${e instanceof Error ? e.message : String(e)}`, { error: e });
  renderBootFailure(e, { appVersion: APP_VERSION });
});
